import { useCallback, useMemo } from 'react';
import { graphql, useFragment, useRelayEnvironment, fetchQuery } from 'react-relay';
import { useSbSelector } from '../../reducers/useSbSelector';
import { type usePartialItems_item$key } from './__generated__/usePartialItems_item.graphql';

/* eslint-disable relay/unused-fields */
const partialItemsQuery = graphql`
    query usePartialItemsQuery(
        $itemIds: [String]!
        $personalizationTypes: [String]!
        $medianL3SoldItemPriceScores: [Float]!
        $isTrade: Boolean!
        $fetchTileVideo: Boolean!
        $showSeller: Boolean!
        $priceBookName: String = ""
        $pageDisplayEnum: PageDisplayEnum = searchAndBrowse
        $userCountryCode: String = ""
    ) {
        viewer {
            itemSearchInflateItems(
                itemIds: $itemIds
                personalizationTypes: $personalizationTypes
                medianL3SoldItemPriceScores: $medianL3SoldItemPriceScores
            ) {
                edges {
                    node {
                        item {
                            serviceId
                            isPartialItem
                            ...FavoritesProvider_item
                            ...useSharedUrgencySignals_item
                            ...useItemShippingPrices_item
                            ...useSbSharedItemTracking_item
                            ...SharedItemTile_item
                                @arguments(
                                    isTrade: $isTrade
                                    fetchVideo: $fetchTileVideo
                                    priceBookName: $priceBookName
                                    showSeller: $showSeller
                                )
                        }
                    }
                }
            }
        }
    }
`;
/* eslint-enable relay/unused-fields */

const itemFragment = graphql`
    fragment usePartialItems_item on Item @relay(plural: true) {
        serviceId
        isPartialItem
        personalizationType
        medianL3SoldItemPriceScore
    }
`;

export const usePartialItems = (
    itemsRef: usePartialItems_item$key
): {
    fetchPartialItems: () => Promise<void>;
    partialItemsIds: string[];
    numOfInflatedItems: number;
    numOfPlaceholderItems: number;
} => {
    const items = useFragment(itemFragment, itemsRef);
    const environment = useRelayEnvironment();
    const {
        numOfItemsToInflate,
        isTrade,
        fetchTileVideo = false,
        userCountryCode,
        priceBookName,
        showSeller,
    } = useSbSelector(state => state.relayVariables.variables);
    const {
        itemIds,
        personalizationTypes,
        medianL3SoldItemPriceScores,
        numOfInflatedItems,
        numOfPlaceholderItems,
    } = useMemo(() => {
        const ids: string[] = [];
        const types: string[] = [];
        const scores: number[] = [];
        const inflatedItemIds = new Set<string>();

        if (numOfItemsToInflate) {
            items.forEach(item => {
                if (item.isPartialItem && item.serviceId) {
                    ids.push(item.serviceId);
                    types.push(item.personalizationType || '');
                    scores.push(item.medianL3SoldItemPriceScore || 0);
                } else if (item.serviceId) {
                    inflatedItemIds.add(item.serviceId);
                }
            });
        }

        // personalizationTypes are coming from item-search response in root call, as I'm inflating data just from itemQuery here, need to send these around to have correct/full data in Relay.
        return {
            itemIds: ids,
            personalizationTypes: types,
            medianL3SoldItemPriceScores: scores,
            numOfInflatedItems: inflatedItemIds.size,
            numOfPlaceholderItems: ids.length ? items.length - inflatedItemIds.size : 0,
        };
    }, [items, numOfItemsToInflate]);

    const fetchPartialItems = useCallback(async (): Promise<void> => {
        if (!itemIds.length) {
            return;
        }
        // need to await here to correctly catch the error in the SbSharedPartialItemsPlaceholder component
        await fetchQuery(environment, partialItemsQuery, {
            itemIds,
            personalizationTypes,
            medianL3SoldItemPriceScores,
            showSeller,
            pageDisplayEnum: 'searchAndBrowse',
            isTrade,
            fetchTileVideo,
            userCountryCode,
            priceBookName,
        }).toPromise();
    }, [
        environment,
        showSeller,
        isTrade,
        fetchTileVideo,
        userCountryCode,
        priceBookName,
        itemIds,
        personalizationTypes,
        medianL3SoldItemPriceScores,
    ]);

    return {
        fetchPartialItems,
        partialItemsIds: itemIds,
        numOfInflatedItems,
        numOfPlaceholderItems,
    };
};
